import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Convert hex color to "R, G, B" string for use in rgba() CSS expressions.
 */
function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '34, 211, 238'; // fallback to default cyan
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
}

/**
 * Theme Presets
 * Each preset defines accent colors + mode (light/dark)
 */
export const THEME_PRESETS = {
    // Dark Themes
    'midnight-cyan': {
        label: 'Midnight Cyan',
        mode: 'dark',
        primaryColor: '#22D3EE',
        secondaryColor: '#0066CC',
        preview: ['#020617', '#22D3EE', '#0066CC']
    },
    'midnight-purple': {
        label: 'Midnight Purple',
        mode: 'dark',
        primaryColor: '#8B5CF6',
        secondaryColor: '#3B82F6',
        preview: ['#0D1117', '#8B5CF6', '#3B82F6']
    },
    'ocean-dark': {
        label: 'Ocean Dark',
        mode: 'dark',
        primaryColor: '#3B82F6',
        secondaryColor: '#06B6D4',
        preview: ['#0D1117', '#3B82F6', '#06B6D4']
    },
    'emerald-dark': {
        label: 'Emerald Dark',
        mode: 'dark',
        primaryColor: '#10B981',
        secondaryColor: '#3B82F6',
        preview: ['#0D1117', '#10B981', '#3B82F6']
    },
    'crimson-dark': {
        label: 'Crimson Dark',
        mode: 'dark',
        primaryColor: '#EF4444',
        secondaryColor: '#F97316',
        preview: ['#0D1117', '#EF4444', '#F97316']
    },

    // Light Themes
    'clean-light': {
        label: 'Clean Light',
        mode: 'light',
        primaryColor: '#6366F1',
        secondaryColor: '#3B82F6',
        preview: ['#FFFFFF', '#6366F1', '#3B82F6']
    },
    'warm-light': {
        label: 'Warm Sand',
        mode: 'light',
        primaryColor: '#D97706',
        secondaryColor: '#EA580C',
        preview: ['#FEFCE8', '#D97706', '#EA580C']
    },
    'forest-light': {
        label: 'Forest Light',
        mode: 'light',
        primaryColor: '#059669',
        secondaryColor: '#0D9488',
        preview: ['#F0FDF4', '#059669', '#0D9488']
    },
    'slate-light': {
        label: 'Slate Professional',
        mode: 'light',
        primaryColor: '#4F46E5',
        secondaryColor: '#7C3AED',
        preview: ['#F8FAFC', '#4F46E5', '#7C3AED']
    }
};

export const useThemeStore = create(
    persist(
        (set, get) => ({
            primaryColor: '#22D3EE',
            secondaryColor: '#0066CC',
            themeMode: 'dark',
            activePreset: 'midnight-cyan',

            setPreset: (presetKey) => {
                const preset = THEME_PRESETS[presetKey];
                if (!preset) return;
                set({
                    activePreset: presetKey,
                    primaryColor: preset.primaryColor,
                    secondaryColor: preset.secondaryColor,
                    themeMode: preset.mode
                });
                get().applyTheme();
            },

            setPrimaryColor: (color) => {
                set({ primaryColor: color, activePreset: 'custom' });
                get().applyTheme();
            },

            setSecondaryColor: (color) => {
                set({ secondaryColor: color, activePreset: 'custom' });
                get().applyTheme();
            },

            setThemeMode: (mode) => {
                set({ themeMode: mode });
                get().applyTheme();
            },

            resetToPreset: (presetKey) => {
                const key = presetKey || 'midnight-cyan';
                const preset = THEME_PRESETS[key];
                if (!preset) return;
                set({
                    activePreset: key,
                    primaryColor: preset.primaryColor,
                    secondaryColor: preset.secondaryColor,
                    themeMode: preset.mode
                });
                get().applyTheme();
            },

            applyTheme: () => {
                const { primaryColor, secondaryColor, themeMode } = get();

                // Set data-theme on html element for CSS variable overrides
                document.documentElement.setAttribute('data-theme', themeMode);

                // Apply accent colors
                document.documentElement.style.setProperty('--accent-primary', primaryColor);
                document.documentElement.style.setProperty('--accent-blue', secondaryColor);
                document.documentElement.style.setProperty('--accent-purple', primaryColor);

                // Regenerate gradient
                const gradient = `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`;
                const gradientHover = `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor}cc 100%)`;
                document.documentElement.style.setProperty('--accent-gradient', gradient);
                document.documentElement.style.setProperty('--accent-gradient-hover', gradientHover);

                // Update glow shadow to match primary
                document.documentElement.style.setProperty(
                    '--shadow-glow',
                    `0 0 20px ${primaryColor}4D`
                );
                document.documentElement.style.setProperty(
                    '--border-accent',
                    `${primaryColor}80`
                );

                // Update RGB triplet for rgba() expressions in CSS
                document.documentElement.style.setProperty(
                    '--accent-primary-rgb',
                    hexToRgb(primaryColor)
                );
            }
        }),
        {
            name: 'alwaysin-theme',
            partialize: (state) => ({
                primaryColor: state.primaryColor,
                secondaryColor: state.secondaryColor,
                themeMode: state.themeMode,
                activePreset: state.activePreset
            })
        }
    )
);
